import * as Yup from 'yup';

import {
  ContributionSelection,
  isSelectableContribution,
  SelectionState,
} from '~support-at-home/lib/contributionSelection';
import { formatServicePeriod } from '~support-at-home/lib/servicePeriod';

import { Column, Row } from '~components/table-view';
import { numberEditor, selectEditor } from '~components/table-view/columnEditors';
import { genericCellRender } from '~components/table-view/columnRenderers';
import ContributionCategoryCell from '~support-at-home/components/config/contributions/ContributionCategoryCell';
import ContributionClientCategoryHeader from '~support-at-home/components/config/contributions/ContributionClientCategoryHeader';
import ContributionClientCell from '~support-at-home/components/config/contributions/ContributionClientCell';
import ContributionRateHeader from '~support-at-home/components/config/contributions/ContributionRateHeader';
import ContributionStatusBadge from '~support-at-home/components/config/contributions/ContributionStatusBadge';
import PeriodEditor, { ContributionPeriod } from '~support-at-home/components/config/contributions/PeriodEditor';
import TruncatedText from '~support-at-home/components/config/contributions/TruncatedText';

interface CategoryLookup {
  id: string;
  name: string;
  max_contrib_proportion?: number;
}

interface FundingSourceLookup {
  id: number;
  name: string;
}

interface ColumnsFactoryParams {
  categories?: CategoryLookup[];
  fundingSources?: FundingSourceLookup[];
  // The browser's local date as `YYYY-MM-DD`, used for the Status column.
  today: string;
  canAddForClient?: (row: Row) => boolean;
  onAddContribution?: (clientId: number) => void;
  // The bulk update selection. Omitted, the first column has no checkboxes.
  selection?: ContributionSelection;
  // The select-all checkbox in the first column's header. Omitted, the header has none.
  headerSelection?: { state: SelectionState; disabled: boolean; onToggle: () => void };
}

// Every column but Funding Source is fixed, so that one absorbs the remaining width. Cells never wrap
// and the table is sized to fit the tab without scrolling sideways: the client name and the category
// share the first column (a client row has no category and a contribution row needs no client), the
// two dates share one Period column, and cell padding is tighter than TableView's default (see
// _configs.scss). The fixed widths fit their longest real value ("01/Nov/2025 - 30/Jun/2026", the
// Current badge); the free-text values cut short with an ellipsis and show in full on hover. A long
// header is shortened and its caption carries the full meaning.
const MAX_WIDTH = '70px';
const PERIOD_WIDTH = '210px';
const STATUS_WIDTH = '80px';

const centeredClassNames = { header: 'header-align-center', cell: 'cell-align-center' };
// The column the page is about, and the one a bulk update changes: tinted (see _configs.scss).
// `column` lands on the cell (td) itself for the tint; `cell` on the value inside it.
const rateClassNames = {
  header: `${centeredClassNames.header} contribution-rate-header`,
  column: 'contribution-rate-column',
  cell: `${centeredClassNames.cell} contribution-rate-cell`,
};

const isContribution = (row: Row) => row?.row_type === 'contribution';

// Client rows only carry the first cell (the client); every other cell is empty.
const onlyForContributions = (render: (value: any, row: Row) => any) => (value: any, row: Row) =>
  isContribution(row) ? render(value, row) : null;

const columnsFactory = ({
  categories = [],
  fundingSources = [],
  today,
  canAddForClient = () => false,
  onAddContribution,
  selection,
  headerSelection,
}: ColumnsFactoryParams): Column[] => {
  const categoryOptions = categories.map(({ id, name }) => ({ id, name }));
  const fundingOptions = fundingSources.map(({ id, name }) => ({ id, name }));

  const categoryColumn: Column = {
    id: 'category_id',
    // The header carries its own caption tooltip (on the label only), so the select-all checkbox beside
    // the label neither opens it nor sorts the column.
    header: ContributionClientCategoryHeader({ selection: headerSelection }),
    sortable: false,
    type: 'select',
    // No inline width: set in _configs.scss, where it narrows on a small table so Funding Source keeps
    // room (the same container query as Contribution %).
    classNames: { header: 'client-category-header' },
    options: categoryOptions,
    isEditable: isContribution,
    renderReadOnly: (_value, row) =>
      isContribution(row)
        ? genericCellRender((_categoryId, contribution) =>
            ContributionCategoryCell({
              row: contribution,
              selection,
              isSelectable: isSelectableContribution(contribution, today),
            }),
          )(_value, row)
        : ContributionClientCell({ row, canAdd: canAddForClient(row), onAdd: onAddContribution, selection }),
    // Snapshot the category onto the row so the Max % column and the contribution validation
    // follow the choice straight away.
    onChange: (row) => {
      const category = categories.find((c) => c.id === row.category_id);
      return category
        ? {
            category: { id: category.id, name: category.name, max_contrib_proportion: category.max_contrib_proportion },
          }
        : { category: undefined };
    },
    validation: Yup.string().nullable().required('Category is required'),
  };
  categoryColumn.renderEditor = selectEditor(categoryOptions, false, categoryColumn);

  const fundingColumn: Column = {
    id: 'funding_source_id',
    header: 'Funding Source',
    caption: 'Where the funding for this contribution comes from',
    type: 'select',
    // Deliberately no width - the only fluid column.
    options: fundingOptions,
    isEditable: isContribution,
    renderReadOnly: onlyForContributions(
      genericCellRender((_value, row) => TruncatedText({ text: row?.funding_source_name ?? '' })),
    ),
    onChange: (row) => ({
      funding_source_name: fundingOptions.find((o) => String(o.id) === String(row.funding_source_id))?.name ?? '',
    }),
    validation: Yup.number().typeError('Funding Source is required').required('Funding Source is required'),
  };
  fundingColumn.renderEditor = selectEditor(
    (row: Row) => {
      const current = row?.funding_source_id;
      // A saved row can point at a funding type that is no longer offered (deleted since). Keep it
      // selectable so an unrelated edit does not silently drop it.
      if (current == null || fundingOptions.some((o) => String(o.id) === String(current))) return fundingOptions;
      return [...fundingOptions, { id: current, name: row.funding_source_name || `Funding source #${current}` }];
    },
    false,
    fundingColumn,
  );

  const contributionColumn: Column = {
    id: 'contrib_proportion',
    header: ContributionRateHeader(),
    caption: 'The share the client contributes. Choose a category first, as it sets the maximum',
    type: 'number',
    // No inline width: the column's width is set in _configs.scss, where it widens along with the
    // header label when the table has room.
    classNames: rateClassNames,
    // Locked until a category is chosen, because the category decides the maximum.
    isEditable: (row) => isContribution(row) && !!row.category_id,
    validation: Yup.number()
      .typeError('Contribution is required')
      .required('Contribution is required')
      .min(0, 'Contribution cannot be negative')
      .test('max-contrib', 'Contribution exceeds the category maximum', function (value) {
        if (value == null) return true;
        const max = this.parent?.category?.max_contrib_proportion;
        return max == null || value <= max;
      }),
  };
  contributionColumn.renderReadOnly = onlyForContributions(
    genericCellRender((value) => (value != null ? String(value) : ''), true, contributionColumn),
  );
  contributionColumn.renderEditor = numberEditor(
    'contrib_proportion',
    0,
    (row: Row) => row?.category?.max_contrib_proportion,
    false,
    contributionColumn,
  );

  const maxColumn: Column = {
    id: 'category.max_contrib_proportion',
    header: 'Max %',
    caption: 'The highest contribution the category allows',
    type: 'number',
    style: { width: MAX_WIDTH },
    classNames: centeredClassNames,
    isEditable: false,
    deriveValue: (row) => row.category?.max_contrib_proportion,
  };
  maxColumn.renderReadOnly = onlyForContributions(
    genericCellRender((value) => (value != null ? String(value) : ''), true, maxColumn),
  );

  const periodColumn: Column = {
    id: 'period',
    header: 'Period',
    caption: 'The first and last day this contribution applies. Ongoing means it has no end date',
    type: 'custom',
    style: { width: PERIOD_WIDTH },
    isEditable: isContribution,
    // The row keeps start_date and end_date as the API fields; `period` mirrors them so the one cell
    // can edit both. deriveValue keeps the mirror current after any change, and onChange writes an edit
    // back to the two real fields.
    deriveValue: (row) => ({ start_date: row.start_date ?? null, end_date: row.end_date ?? null }),
    onChange: (row) => {
      const period = row.period as ContributionPeriod | undefined;
      return { start_date: period?.start_date ?? null, end_date: period?.end_date ?? null };
    },
    renderReadOnly: onlyForContributions(
      genericCellRender((_value, row) => formatServicePeriod(row?.start_date, row?.end_date)),
    ),
    renderEditor: (_value, row, onChange, _context, isDisabled) =>
      PeriodEditor({ row, onChange, disabled: isDisabled }),
    validation: Yup.mixed()
      .test('start-required', 'Start date is required', function () {
        return !!this.parent?.start_date;
      })
      .test('end-on-or-after-start', 'End date must be on or after the start date', function () {
        const { start_date, end_date } = this.parent ?? {};
        if (!end_date || !start_date) return true;
        return String(end_date).slice(0, 10) >= String(start_date).slice(0, 10);
      }),
  };

  const statusColumn: Column = {
    id: 'status',
    header: 'Status',
    caption: 'Current, Future (not started yet) or Ended, as of today',
    type: 'readonly',
    style: { width: STATUS_WIDTH },
    isEditable: false,
    renderReadOnly: onlyForContributions((_value, row) => ContributionStatusBadge({ row, today })),
  };

  return [categoryColumn, fundingColumn, contributionColumn, maxColumn, periodColumn, statusColumn];
};

export default columnsFactory;
